import { useEffect, useState } from "react";
import { api, errorText } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { PageTitle } from "./AdminOverview";
import { Field, FormError, FormSuccess } from "@/components/brand/Field";

export default function AdminAdmins() {
  const { admin } = useAdminAuth();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ email: "", full_name: "", role: "support", password: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.get("/admin/auth/admins").then((r) => setList(r.data));
  useEffect(() => { load().catch((e) => setErr(errorText(e))); }, []);

  const run = async (fn, ok) => { setMsg(""); setErr(""); try { await fn(); await load(); setMsg(ok); } catch (e) { setErr(errorText(e)); } };

  return (
    <>
      <PageTitle eyebrow="Access" title="Admin accounts" />
      <FormSuccess message={msg} testId="admins-success" />
      <FormError message={err} testId="admins-error" />
      <div className="ts-card p-6 mb-6">
        <h2 className="text-base md:text-lg font-semibold mb-4">Invite an admin</h2>
        <form className="grid sm:grid-cols-2 gap-x-4" onSubmit={(e) => { e.preventDefault(); run(() => api.post("/admin/auth/admins", form), "Admin created. They will set up MFA at first sign in."); setForm({ email: "", full_name: "", role: "support", password: "" }); }}>
          <Field id="a-name" label="Name"><input id="a-name" className="ts-input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="new-admin-name-input" /></Field>
          <Field id="a-email" label="Email"><input id="a-email" className="ts-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-admin-email-input" /></Field>
          <Field id="a-role" label="Role">
            <select id="a-role" className="ts-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-admin-role-select">
              <option value="owner">Owner / Superadmin</option><option value="support">Support admin</option><option value="analyst">Read-only analyst</option>
            </select>
          </Field>
          <Field id="a-pass" label="Temporary password" hint="At least 12 characters. Share it securely; they should reset it."><input id="a-pass" className="ts-input" type="text" minLength={12} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-admin-password-input" /></Field>
          <div className="sm:col-span-2"><button className="ts-btn ts-btn-primary" data-testid="new-admin-submit">Create admin</button></div>
        </form>
      </div>
      <div className="ts-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left eyebrow" style={{ background: "var(--paper)" }}><th className="p-3">Admin</th><th>Role</th><th>MFA</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody data-testid="admins-table">
            {list.map((a) => (
              <tr key={a.id} className="border-t" style={{ borderColor: "var(--line-soft)" }} data-testid={`admin-row-${a.email}`}>
                <td className="p-3"><div className="font-medium">{a.full_name}</div><div className="text-xs" style={{ color: "var(--muted-text)" }}>{a.email}</div></td>
                <td>
                  <select className="ts-input h-8 text-xs w-36" value={a.role} disabled={a.id === admin.id} onChange={(e) => run(() => api.patch(`/admin/auth/admins/${a.id}`, { role: e.target.value }), "Role updated.")} aria-label={`Role for ${a.email}`}>
                    <option value="owner">owner</option><option value="support">support</option><option value="analyst">analyst</option>
                  </select>
                </td>
                <td>{a.mfa_enrolled ? <span style={{ color: "var(--blue)" }}>Enrolled</span> : <span style={{ color: "var(--amber)" }}>Pending</span>}</td>
                <td className="num text-xs">{a.status}</td>
                <td className="whitespace-nowrap">
                  {a.id !== admin.id && (
                    <>
                      <button className="ts-link text-xs mr-3" onClick={() => run(() => api.patch(`/admin/auth/admins/${a.id}`, { status: a.status === "active" ? "disabled" : "active" }), "Status updated.")}>{a.status === "active" ? "Disable" : "Enable"}</button>
                      <button className="ts-link text-xs" onClick={() => run(() => api.post(`/admin/auth/admins/${a.id}/reset-mfa`), "MFA reset — they will re-enrol at next sign in.")}>Reset MFA</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
